<template>
	<div class="map-root">
		<div ref="mapContainer" class="map-container"></div>
		<div
			v-if="tooltipVisible && tooltipData"
			class="task-tooltip"
			:style="{ left: `${tooltipPosition.x}px`, top: `${tooltipPosition.y}px` }"
		>
			<div>任务名称：{{ tooltipData.taskName || '--' }}</div>
			<div>负责人：{{ tooltipData.person || '--' }}</div>
			<div>任务类型：{{ tooltipData.taskType || '--' }}</div>
			<div>状态：{{ changeStatus(tooltipData.status) }}</div>
			<div>日期：{{ formatTaskDate(tooltipData.createDate) }}</div>
		</div>
	</div>
</template>

<script>
	import 'ol/ol.css';
	import { Map, View } from 'ol';
	import TileLayer from 'ol/layer/Tile';
	import XYZ from 'ol/source/XYZ';
	import { fromLonLat } from 'ol/proj';
	import VectorLayer from 'ol/layer/Vector';
	import VectorSource from 'ol/source/Vector';
	import Feature from 'ol/Feature';
	import Point from 'ol/geom/Point';
	import Polygon from 'ol/geom/Polygon';
	import LineString from 'ol/geom/LineString';
	import { Fill, Stroke, Style, Icon } from 'ol/style';

	export default {
		name: 'FarmingNewMap',
		props: {
			zoom: {
				type: Number,
				default: 15.1,
			},
		},
		data() {
			return {
				map: null,
				gansuGeoJSONUrl: 'https://qgst.obs.cn-southwest-2.myhuaweicloud.com/rabbit/pc/geoJson/%E7%94%98%E8%82%83%E7%9C%813.geoJson',
				gansuBorderCoords: [],
				viewConfig2D: {
					center: [100.362315, 38.9074672],
					zoom: this.zoom,
				},
				plotSource: null,
				plotLayer: null,
				waterPipeSource: null,
				waterPipeLayer: null,
				markerSource: null,
				markerLayer: null,
				taskMarkerSource: null,
				taskMarkerLayer: null,
				plotItems: [],
				selectedPlotId: null,
				tooltipVisible: false,
				tooltipData: null,
				tooltipPosition: {
					x: 0,
					y: 0,
				},
			};
		},
		mounted() {
			this.initMap();
			this.loadGansuGeoJSON();
			this.loadPlotData();
		},
		beforeDestroy() {
			if (this.map) {
				this.map.setTarget(null);
				this.map.dispose();
			}
		},
		methods: {
			// 初始化 2D 地图与各业务图层。
			initMap() {
				const tileLayer = new TileLayer({
					source: new XYZ({
						url: 'https://map.snkoudai.com/maps/vt/lyrs=s&hl=zh-CN&gl=cn&scale=1&x={x}&y={y}&z={z}',
						maxZoom: 18,
					}),
				});

				this.map = new Map({
					target: this.$refs.mapContainer,
					layers: [tileLayer],
					view: new View({
						center: fromLonLat(this.viewConfig2D.center),
						zoom: this.viewConfig2D.zoom,
					}),
				});

				this.initPlotLayer();
				this.initWaterPipeLayer();
				this.initMarkerLayer();
				this.initTaskMarkerLayer();
				this.addStaticMarkers();
				this.bindMapEvents();
			},

			// 统一处理地块点击、任务点悬停和静态点位交互。
			bindMapEvents() {
				this.map.on('singleclick', (event) => {
					const feature = this.map.forEachFeatureAtPixel(
						event.pixel,
						(currentFeature) => currentFeature,
						{ hitTolerance: 6 }
					);

					if (!feature) {
						this.clearPlotSelection();
						return;
					}

					const featureType = feature.get('featureType');
					if (featureType === 'task-marker') {
						return;
					}

					if (featureType === 'static-marker') {
						this.handleStaticMarkerClick(feature);
						return;
					}

					if (featureType === 'plot-polygon') {
						this.handlePlotClick(feature);
						return;
					}

					this.clearPlotSelection();
				});

				this.map.on('pointermove', (event) => {
					if (event.dragging) return;

					const feature = this.map.forEachFeatureAtPixel(
						event.pixel,
						(currentFeature) => currentFeature,
						{ hitTolerance: 6 }
					);

					const featureType = feature ? feature.get('featureType') : '';
					const isClickable = ['task-marker', 'static-marker', 'plot-polygon'].includes(featureType);
					this.map.getTargetElement().style.cursor = isClickable ? 'pointer' : '';

					if (featureType === 'task-marker') {
						this.showTaskTooltip(feature.get('taskData'), event.pixel);
						return;
					}

					this.hideTaskTooltip();
				});
			},

			// 地块面图层，对应原 farmingMap.vue 的 Polygon 区块。
			initPlotLayer() {
				this.plotSource = new VectorSource();
				this.plotLayer = new VectorLayer({
					source: this.plotSource,
					zIndex: 26,
					name: '地块图层',
				});
				this.map.addLayer(this.plotLayer);
			},

			// 管线图层使用双线叠加，保持原页面的亮边效果。
			initWaterPipeLayer() {
				this.waterPipeSource = new VectorSource();
				this.waterPipeLayer = new VectorLayer({
					source: this.waterPipeSource,
					zIndex: 27,
					name: '管线图层',
					style: (feature) => {
						// const pipeType = feature.get('pipeType');
						// if (pipeType === 'inner') {
						// 	return new Style({
						// 		stroke: new Stroke({
						// 			color: '#005ACF',
						// 			width: 3,
						// 			lineCap: 'round',
						// 			lineJoin: 'round',
						// 		}),
						// 	});
						// }

						// return new Style({
						// 	stroke: new Stroke({
						// 		color: '#97EDFF',
						// 		width: 5,
						// 		lineCap: 'round',
						// 		lineJoin: 'round',
						// 	}),
						// });
					},
				});
				this.map.addLayer(this.waterPipeLayer);
			},

			// 静态点位图层，用于双堡村和张掖市标记。
			initMarkerLayer() {
				this.markerSource = new VectorSource();
				this.markerLayer = new VectorLayer({
					source: this.markerSource,
					zIndex: 40,
					name: '静态标记图层',
				});
				this.map.addLayer(this.markerLayer);
			},

			// 任务点位图层，供父组件通过 setMarkers 动态更新。
			initTaskMarkerLayer() {
				this.taskMarkerSource = new VectorSource();
				this.taskMarkerLayer = new VectorLayer({
					source: this.taskMarkerSource,
					zIndex: 41,
					name: '任务标记图层',
				});
				this.map.addLayer(this.taskMarkerLayer);
			},

			// 单独维护 2D 视角，避免后续场景调整时散落硬编码。
			apply2DView() {
				if (!this.map) return;
				const view = this.map.getView();
				view.setCenter(fromLonLat(this.viewConfig2D.center));
				view.setZoom(this.viewConfig2D.zoom);
			},

			// 加载甘肃边界 GeoJSON，生成外围遮罩和发光围栏。
			async loadGansuGeoJSON() {
				try {
					const response = await fetch(this.gansuGeoJSONUrl);
					if (!response.ok) {
						throw new Error(`HTTP error! status: ${response.status}`);
					}

					const geojsonData = await response.json();
					this.gansuBorderCoords = this.extractCoordinatesFromGeoJSON(geojsonData);
					if (!this.gansuBorderCoords.length) return;

					this.createMaskLayer();
					this.createFenceEffect();
				} catch (error) {
					console.error('❌ 加载 GeoJSON 失败:', error);
					this.createFallbackMask();
				}
			},

			// 提取 GeoJSON 外环坐标，供遮罩和围栏复用。
			extractCoordinatesFromGeoJSON(geojson) {
				const coordinates = [];

				const extractFromGeometry = (geometry) => {
					if (!geometry) return;

					if (geometry.type === 'Polygon') {
						const exteriorRing = geometry.coordinates[0];
						if (exteriorRing && exteriorRing.length > 0) {
							coordinates.push(exteriorRing.slice(0, -1));
						}
					} else if (geometry.type === 'MultiPolygon') {
						geometry.coordinates.forEach((polygonCoords) => {
							const exteriorRing = polygonCoords[0];
							if (exteriorRing && exteriorRing.length > 0) {
								coordinates.push(exteriorRing.slice(0, -1));
							}
						});
					}
				};

				if (geojson.type === 'FeatureCollection') {
					geojson.features.forEach((feature) => extractFromGeometry(feature.geometry));
				} else if (geojson.type === 'Feature') {
					extractFromGeometry(geojson.geometry);
				} else {
					extractFromGeometry(geojson);
				}

				return coordinates;
			},

			// 外围遮罩用于压暗非目标区域，突出农事主场景。
			createMaskLayer() {
				const worldBoundsProjected = [
					[-180, -90],
					[180, -90],
					[180, 90],
					[-180, 90],
					[-180, -90],
				].map((coord) => fromLonLat(coord));

				const gansuCoordsProjected = this.gansuBorderCoords.map((ring) =>
					ring.map((coord) => fromLonLat(coord))
				);

				const maskFeature = new Feature({
					geometry: new Polygon([worldBoundsProjected, ...gansuCoordsProjected]),
				});

				maskFeature.setStyle(
					new Style({
						fill: new Fill({ color: 'rgba(35, 54, 34, 0.78)' }),
						stroke: new Stroke({ color: 'rgba(35, 54, 34, 0.18)', width: 1 }),
					})
				);

				this.map.addLayer(
					new VectorLayer({
						source: new VectorSource({ features: [maskFeature] }),
						zIndex: 20,
						name: '遮罩层',
					})
				);
			},

			// 发光围栏拆成内外两层线，模拟原地图边界强调效果。
			createFenceEffect() {
				this.removeFenceLayers();

				this.gansuBorderCoords.forEach((ring) => {
					const closedRing = [...ring, ring[0]];
					const borderCoords = closedRing.map((coord) => fromLonLat(coord));
					const glowFeature = new Feature({ geometry: new LineString(borderCoords) });
					glowFeature.setStyle(
						new Style({
							stroke: new Stroke({
								color: 'rgba(0, 255, 136, 0.3)',
								width: 12,
								lineDash: [12, 8],
							}),
						})
					);

					const borderFeature = new Feature({ geometry: new LineString(borderCoords) });
					borderFeature.setStyle(
						new Style({
							stroke: new Stroke({
								color: '#00ff88',
								width: 4,
								lineDash: [12, 8],
							}),
						})
					);

					this.map.addLayer(
						new VectorLayer({
							source: new VectorSource({ features: [glowFeature] }),
							zIndex: 24,
							name: '发光围栏',
						})
					);

					this.map.addLayer(
						new VectorLayer({
							source: new VectorSource({ features: [borderFeature] }),
							zIndex: 25,
							name: '围栏层',
						})
					);
				});
			},

			// 围栏重绘前先清理旧图层，避免重复叠加。
			removeFenceLayers() {
				if (!this.map) return;
				const layers = this.map.getLayers().getArray();
				const toRemove = [];
				layers.forEach((layer) => {
					const name = layer.get('name');
					if (name === '围栏层' || name === '发光围栏') {
						toRemove.push(layer);
					}
				});
				toRemove.forEach((layer) => this.map.removeLayer(layer));
			},

			// GeoJSON 加载失败时，使用简化边界兜底避免页面全空。
			createFallbackMask() {
				const gansuSimulated = [
					[94.0, 38.0], [95.0, 39.0], [96.0, 40.0], [97.0, 41.0], [98.0, 42.0], [99.0, 41.5],
					[100.0, 42.0], [101.0, 42.5], [102.0, 42.0], [103.0, 41.0], [104.0, 40.0], [105.0, 39.0],
					[106.0, 38.0], [107.0, 37.0], [108.0, 36.0], [109.0, 35.0], [108.0, 34.0], [107.0, 33.0],
					[106.0, 34.0], [105.0, 35.0], [104.0, 36.0], [103.0, 37.0], [102.0, 38.0], [101.0, 39.0],
					[100.0, 40.0], [99.0, 41.0], [98.0, 41.0], [97.0, 40.0], [96.0, 39.0], [95.0, 38.5], [94.0, 38.0],
				];

				const worldProjected = [
					[-180, -90], [180, -90], [180, 90], [-180, 90], [-180, -90],
				].map((coord) => fromLonLat(coord));
				const gansuProjected = gansuSimulated.map((coord) => fromLonLat(coord));

				const maskFeature = new Feature({
					geometry: new Polygon([worldProjected, gansuProjected]),
				});
				maskFeature.setStyle(
					new Style({
						fill: new Fill({ color: 'rgba(35, 54, 34, 0.78)' }),
						stroke: new Stroke({ color: 'rgba(35, 54, 34, 0.18)', width: 1 }),
					})
				);

				this.map.addLayer(
					new VectorLayer({
						source: new VectorSource({ features: [maskFeature] }),
						zIndex: 20,
						name: '遮罩层',
					})
				);
			},

			// 拉取农事地块数据，并转换成当前 2D 地图要素。
			async loadPlotData() {
				if (!this.$request) return;
				try {
					const response = await this.$request('iot/map/water');
					const mapData = Array.isArray(response?.data) ? response.data : [];
					this.plotItems = mapData;
					this.renderPlots(mapData);
				} catch (error) {
					console.error('❌ 加载农事地块失败:', error);
				}
			},

			// 批量渲染地块面，同时补充每块地的灌溉管线。
			renderPlots(mapData) {
				if (!this.plotSource || !this.waterPipeSource) return;
				this.plotSource.clear();
				this.waterPipeSource.clear();

				const dkColor = ['rgb(37, 170, 128)', '#7ebe7a', '#6c924f', '#3f8a3b'];

				for (let i = 0; i < mapData.length; i++) {
					const item = mapData[i];
					if (!item?.latLonPath) continue;

					try {
						const data = JSON.parse(item.latLonPath);
						if (!Array.isArray(data) || !data.length) continue;

						const polygonPath = data.map((point) => fromLonLat([point.longitude, point.latitude]));
						const firstPoint = polygonPath[0];
						const lastPoint = polygonPath[polygonPath.length - 1];
						const isClosed = firstPoint && lastPoint && firstPoint[0] === lastPoint[0] && firstPoint[1] === lastPoint[1];
						const ring = isClosed ? polygonPath : [...polygonPath, firstPoint];

						const feature = new Feature({
							geometry: new Polygon([ring]),
							featureType: 'plot-polygon',
							plotData: item,
							plotColor: dkColor[i % dkColor.length],
						});

						feature.setStyle(this.getPlotStyle(feature));
						this.plotSource.addFeature(feature);
						this.addWaterPipes(item);
					} catch (error) {
						console.warn('⚠️ 地块解析失败:', item, error);
					}
				}
			},

			// 原 waterPipe 字段仍按 # 分段，逐段转换成双层折线。
			addWaterPipes(plotItem) {
				if (!plotItem?.waterPipe) return;
				const pipeList = plotItem.waterPipe.split('#').filter(Boolean);

				pipeList.forEach((pipeItem) => {
					try {
						const data = JSON.parse(pipeItem);
						if (!Array.isArray(data) || !data.length) return;

						const path = data.map((point) => fromLonLat([point.longitude, point.latitude]));
						this.waterPipeSource.addFeature(
							new Feature({ geometry: new LineString(path), pipeType: 'outer' })
						);
						this.waterPipeSource.addFeature(
							new Feature({ geometry: new LineString(path), pipeType: 'inner' })
						);
					} catch (error) {
						console.warn('⚠️ 管线解析失败:', plotItem, error);
					}
				});
			},

			// 选中地块使用橙色描边，其余保持默认白边。
			getPlotStyle(feature) {
				const plotData = feature.get('plotData') || {};
				const plotColor = feature.get('plotColor') || '#3f8a3b';
				const isSelected = plotData.id === this.selectedPlotId;

				return new Style({
					stroke: new Stroke({
						color: isSelected ? '#f6ac00' : '#ffffff',
						width: isSelected ? 5 : 2,
					}),
					fill: new Fill({
						color: this.hexToRgba(plotColor, 0.3),
					}),
				});
			},

			// 点击地块后同步高亮，并通知父组件刷新中心数据。
			handlePlotClick(feature) {
				const plotData = feature.get('plotData');
				this.selectedPlotId = plotData?.id || null;
				this.refreshPlotStyles();
				this.$emit('changeId', this.selectedPlotId || '');
			},

			// 点击空白区域时取消选中状态。
			clearPlotSelection() {
				if (!this.selectedPlotId) return;
				this.selectedPlotId = null;
				this.refreshPlotStyles();
				this.$emit('changeId', '');
			},

			// 地块样式依赖当前选中态，更新时统一重刷整层。
			refreshPlotStyles() {
				if (!this.plotSource) return;
				this.plotSource.getFeatures().forEach((feature) => {
					feature.setStyle(this.getPlotStyle(feature));
				});
			},

			// 保留原页面两个固定地点标记。
			addStaticMarkers() {
				this.addStaticMarker({
					position: [100.362315, 38.904672],
					icon: 'https://ica.obs.cn-south-1.myhuaweicloud.com/screen/home/%E5%8F%8C%E5%A0%A1%E6%9D%91%E5%9C%B0%E5%9D%80%402x.png',
					width: 134,
					height: 134,
					markerCode: 'main-village',
				});

				this.addStaticMarker({
					position: [101.262315, 40.504672],
					icon: 'https://ica.obs.cn-south-1.myhuaweicloud.com/screen/home/%E5%BC%A0%E6%8E%96%E5%B8%82.png',
					width: 26,
					height: 30,
					markerCode: 'zhangye-city',
				});
			},

			// 静态点位统一走图片图标，便于后续继续扩展。
			addStaticMarker({ position, icon, width, height, markerCode }) {
				if (!this.markerSource) return;

				const feature = new Feature({
					geometry: new Point(fromLonLat(position)),
					featureType: 'static-marker',
					markerCode,
					markerPosition: position,
				});

				this.applyIconStyle(feature, icon, width, height);
				this.markerSource.addFeature(feature);
			},

			// 双堡村点位点击后回到主区域视角。
			handleStaticMarkerClick(feature) {
				const markerCode = feature.get('markerCode');
				const markerPosition = feature.get('markerPosition');
				if (markerCode === 'main-village') {
					this.map.getView().setZoom(15);
					this.map.getView().setCenter(fromLonLat(markerPosition));
				}
			},

			// 保持与 farming.vue 现有调用兼容。
			setMarkers(response) {
				if (!this.taskMarkerSource) return;
				this.taskMarkerSource.clear();

				const list = Array.isArray(response?.data) ? response.data : [];
				list.forEach((item) => {
					const config = this.getTaskMarkerConfig(item.status);
					const feature = new Feature({
						geometry: new Point(fromLonLat([item.lon, item.lat])),
						featureType: 'task-marker',
						taskData: item,
					});

					this.applyIconStyle(feature, config.image, config.width, config.height);
					this.taskMarkerSource.addFeature(feature);
				});
			},

			// 不同任务状态对应不同颜色的农事标记图标。
			getTaskMarkerConfig(status) {
				switch (status) {
					case 1:
						return {
							image: 'https://ica.obs.cn-south-1.myhuaweicloud.com/screen/%E5%86%9C%E4%BA%8B/%E7%82%B9%E6%A0%87%E8%AE%B0%E5%B7%B2%E5%AE%8C%E6%88%90%402x.png',
							width: 42,
							height: 60,
						};
					case 2:
						return {
							image: 'https://ica.obs.cn-south-1.myhuaweicloud.com/screen/%E5%86%9C%E4%BA%8B/%E7%82%B9%E6%A0%87%E8%AE%B0%E8%BF%9B%E8%A1%8C%E4%B8%AD%402x.png',
							width: 42,
							height: 60,
						};
					case 3:
						return {
							image: 'https://ica.obs.cn-south-1.myhuaweicloud.com/screen/%E5%86%9C%E4%BA%8B/%E7%82%B9%E6%A0%87%E8%AE%B0%E6%9C%AA%E5%BC%80%E5%A7%8B%402x.png',
							width: 42,
							height: 60,
						};
					case 4:
						return {
							image: 'https://ica.obs.cn-south-1.myhuaweicloud.com/screen/%E5%86%9C%E4%BA%8B/%E7%82%B9%E6%A0%87%E8%AE%B0%E5%B7%B2%E8%B6%85%E6%9C%9F%402x.png',
							width: 42,
							height: 60,
						};
					default:
						return {
							image: 'https://ica.obs.cn-south-1.myhuaweicloud.com/screen/%E5%86%9C%E4%BA%8B/%E7%82%B9%E6%A0%87%E8%AE%B0%E6%9C%AA%E5%BC%80%E5%A7%8B%402x.png',
							width: 42,
							height: 60,
						};
				}
			},

			// 按目标尺寸缩放远程图片，统一用于所有 marker 类型。
			applyIconStyle(feature, icon, width, height) {
				const image = new Image();
				image.onload = () => {
					feature.setStyle(
						new Style({
							image: new Icon({
								src: icon,
								scale: [width / image.width, height / image.height],
								anchor: [0.5, 1],
							}),
						})
					);
				};
				image.src = icon;
			},

			// 悬停任务点时显示概览信息，替代原高德 InfoWindow。
			showTaskTooltip(data, pixel) {
				this.tooltipData = data;
				this.tooltipVisible = true;
				this.tooltipPosition = {
					x: pixel[0] + 24,
					y: pixel[1] - 170,
				};
			},

			// 鼠标离开任务点后隐藏提示框。
			hideTaskTooltip() {
				this.tooltipVisible = false;
				this.tooltipData = null;
			},

			// 沿用项目里的时间格式化工具，保证列表与地图提示一致。
			formatTaskDate(date) {
				if (!date) return '--';
				if (this.utils && typeof this.utils.changeTimeNoHms === 'function') {
					return this.utils.changeTimeNoHms(date);
				}
				return date;
			},

			// 任务状态文案与原 farmingMap.vue 保持一致。
			changeStatus(status) {
				switch (status) {
					case 1:
						return '已完成';
					case 2:
						return '进行中';
					case 3:
						return '未开始';
					case 4:
						return '已超期';
					default:
						return '--';
				}
			},

			// 兼容 rgb/hex 两种写法，统一转成带透明度的颜色。
			hexToRgba(color, alpha) {
				if (color.startsWith('rgb')) {
					return color.replace('rgb(', 'rgba(').replace(')', `, ${alpha})`);
				}

				let hex = color.replace('#', '');
				if (hex.length === 3) {
					hex = hex.split('').map((char) => char + char).join('');
				}

				const r = parseInt(hex.slice(0, 2), 16);
				const g = parseInt(hex.slice(2, 4), 16);
				const b = parseInt(hex.slice(4, 6), 16);
				return `rgba(${r}, ${g}, ${b}, ${alpha})`;
			},
		},
	};
</script>

<style scoped>
	.map-root {
		position: fixed;
		left: 0;
		top: 0;
		width: 100vw;
		height: 100vh;
		z-index: 1;
	}

	.map-container {
		width: 100vw;
		height: 100vh;
		position: fixed;
		left: 0;
		top: 0;
		z-index: 1;
	}

	.task-tooltip {
		position: absolute;
		z-index: 120;
		min-width: 300px;
		padding: 14px 16px;
		background-color: rgba(115, 91, 20, 0.86);
		box-shadow: 0 1px 6px 0 rgba(255, 255, 255, 0.3);
		border: 1px solid rgba(255, 255, 255, 0.2);
		color: rgba(255, 255, 255, 0.88);
		font-size: 15px;
		line-height: 1.8;
		pointer-events: none;
		box-sizing: border-box;
	}
</style>
